import {
  Box,
  Flex,
  Heading,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  ComplianceCoverageBranch,
  SupplierComplianceCoverage,
  SupplierComplianceCoverageStatus,
} from '@texas/api/endpoints/variantsApi';
import {
  compareEnum,
  ensureEnumNumber,
} from '@texas/utils/helpers/enumHelpers';
import { clientEndpoints } from '@texas/clientEndpoints';
import { complianceDocumentKindLabel } from '@texas/components/configuration/complianceDocumentDefinitions/translations';
import { complianceCoverageStatusLabel } from './translations';

interface Props {
  branch: ComplianceCoverageBranch;
}

const statusColors: Record<SupplierComplianceCoverageStatus, string> = {
  [SupplierComplianceCoverageStatus.Valid]: 'status.success',
  [SupplierComplianceCoverageStatus.Expired]: 'status.danger',
  [SupplierComplianceCoverageStatus.NotYetValid]: 'status.warning',
  [SupplierComplianceCoverageStatus.Missing]: 'status.danger',
};

export function ComplianceCoverageTable({ branch }: Props) {
  const { t } = useTranslation();

  return (
    <Box
      as="section"
      bg="entityDirectory.surface"
      border="1px solid"
      borderColor="entityDirectory.border"
    >
      <Flex
        align="center"
        justify="space-between"
        gap={3}
        px={5}
        py={4}
        wrap="wrap"
        borderBottom="1px solid"
        borderColor="entityDirectory.border"
      >
        <Flex align="baseline" gap={3}>
          <Heading as="h2" fontFamily="barlow" fontSize="20px">
            {branch.identifier}
          </Heading>
          <Text fontSize="13px" color="entityDirectory.mutedText">
            {branch.name}
          </Text>
        </Flex>
        <Text fontSize="xs" color="entityDirectory.mutedText">
          {t('supplierComplianceCoverage.branchSummary', {
            requirements: t('supplierComplianceCoverage.requirementCount', {
              count: branch.requirements.length,
            }),
            suppliers: t('supplierComplianceCoverage.supplierCount', {
              count: branch.suppliers.length,
            }),
          })}
        </Text>
      </Flex>
      {branch.requirements.length === 0 ? (
        <Text p={5} fontSize="sm" color="entityDirectory.mutedText">
          {t('supplierComplianceCoverage.none')}
        </Text>
      ) : (
        <>
          {branch.suppliers.length === 0 && (
            <Text px={5} pt={4} fontSize="sm" color="entityDirectory.mutedText">
              {t('supplierComplianceCoverage.noSuppliers')}
            </Text>
          )}
          <Box overflowX="auto">
            <Table
              w="full"
              minW={`${220 + branch.suppliers.length * 240}px`}
              sx={{ tableLayout: 'fixed' }}
            >
              <Thead bg="entityDirectory.inputBackground">
                <Tr>
                  <Th
                    w="29%"
                    px={5}
                    py={4}
                    fontSize="11px"
                    color="entityDirectory.mutedText"
                  >
                    {t('supplierComplianceCoverage.requirement')}
                  </Th>
                  {branch.suppliers.map((supplier) => (
                    <Th
                      key={supplier.id}
                      px={5}
                      py={4}
                      textTransform="none"
                      letterSpacing="normal"
                    >
                      <Text
                        fontSize="13px"
                        fontWeight="semibold"
                        color="entityDirectory.text"
                      >
                        {supplier.name}
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="normal"
                        color={
                          branch.requirements.every((requirement) =>
                            supplier.coverage.some(
                              (item) =>
                                item.definitionId ===
                                  requirement.definitionId &&
                                compareEnum(
                                  SupplierComplianceCoverageStatus,
                                  item.status,
                                  SupplierComplianceCoverageStatus.Valid,
                                ),
                            ),
                          )
                            ? 'status.success'
                            : 'status.danger'
                        }
                        mt={1}
                      >
                        {t('supplierComplianceCoverage.validCount', {
                          valid: branch.requirements.filter((requirement) =>
                            supplier.coverage.some(
                              (item) =>
                                item.definitionId ===
                                  requirement.definitionId &&
                                compareEnum(
                                  SupplierComplianceCoverageStatus,
                                  item.status,
                                  SupplierComplianceCoverageStatus.Valid,
                                ),
                            ),
                          ).length,
                          total: branch.requirements.length,
                        })}
                      </Text>
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {branch.requirements.map((requirement) => (
                  <Tr key={requirement.definitionId}>
                    <Th
                      scope="row"
                      px={5}
                      py={4}
                      textTransform="none"
                      letterSpacing="normal"
                      fontWeight="normal"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="entityDirectory.text"
                      >
                        {requirement.name}
                      </Text>
                      <Text
                        fontSize="xs"
                        color="entityDirectory.mutedText"
                        mt={1}
                      >
                        {complianceDocumentKindLabel(requirement.kind, t)}
                      </Text>
                    </Th>
                    {branch.suppliers.map((supplier) => (
                      <Td key={supplier.id} px={5} py={4} verticalAlign="top">
                        <CoverageCell
                          coverage={supplier.coverage.find(
                            (item) =>
                              item.definitionId === requirement.definitionId,
                          )}
                        />
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}

function CoverageCell({
  coverage,
}: {
  coverage: SupplierComplianceCoverage | undefined;
}) {
  const { t } = useTranslation();
  const status = ensureEnumNumber(
    SupplierComplianceCoverageStatus,
    coverage?.status ?? SupplierComplianceCoverageStatus.Missing,
  );
  const document = coverage?.document;

  return (
    <Box>
      <Flex align="baseline" gap={0}>
        <Text
          w="88px"
          flexShrink={0}
          fontSize="13px"
          fontWeight="medium"
          color={statusColors[status]}
        >
          {complianceCoverageStatusLabel(status, t)}
        </Text>
        {document && (
          <Link
            href={clientEndpoints.downloadFile(document.fileIdentifier)}
            isExternal={true}
            title={document.label}
            fontSize="xs"
            textDecoration="underline"
          >
            {t('supplierComplianceCoverage.viewDocument')}
          </Link>
        )}
      </Flex>
      <Text fontSize="xs" color="entityDirectory.mutedText" mt={1.5}>
        {document
          ? t('supplierComplianceCoverage.dates', {
              start: document.startDate
                ? dayjs(document.startDate).locale('en').format('DD MMM YYYY')
                : t('supplierComplianceCoverage.noStart'),
              end: document.endDate
                ? dayjs(document.endDate).locale('en').format('DD MMM YYYY')
                : t('supplierComplianceCoverage.noExpiry'),
            })
          : t('supplierComplianceCoverage.noDocument')}
      </Text>
    </Box>
  );
}
